import { ChangeDetectionStrategy, Component, effect, ElementRef, inject, input, model, NgZone, OnDestroy, OnInit, output, signal, viewChild } from '@angular/core';
// @ts-ignore
import { untracked } from '@angular/core/primitives/signals';
import 'monaco-editor/esm/vs/basic-languages/sql/sql.contribution';
import * as contentWidgets from 'monaco-editor/esm/vs/editor/browser/viewParts/contentWidgets/contentWidgets.js';
import * as suggestWidgetRenderer from 'monaco-editor/esm/vs/editor/contrib/suggest/browser/suggestWidgetRenderer.js';
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';
import { debounce } from 'ts-debounce';
import { debounced } from '../pbir/utils/debounced';
import { logger } from '../pbir/utils/logger';
import { insightsQueryExpressionLanguage } from './insights-query-expression';

const LOGGER = logger('MonacoEditorComponent');

const MONACO_EDITOR_SOURCE = 'MonacoEditorComponent';

let _editorHacksInstalled = false;

@Component({
  selector: 'monaco-editor',
  template: `
    <div #editorContainer class="editor-container"></div>
  `,
  styleUrls: ['./monaco-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.readonly]': 'readonly',
    '[class.disabled]': 'disabled'
  },
  standalone: false
})
export class MonacoEditorComponent implements OnDestroy, OnInit {
  static defaultConfig: monaco.editor.IStandaloneEditorConstructionOptions = {
    automaticLayout: false,
    fontFamily: "Consolas, Menlo, Courier, monoscape",
    fontSize: 14,
    lineHeight: 14,
    suggestLineHeight: 14,
    suggestFontSize: 12,
    fontLigatures: false,
    scrollBeyondLastLine: false,
    autoClosingBrackets: 'always',
    autoClosingQuotes: 'always',
    dragAndDrop: false,
    useShadowDOM: false,
  };

  readonly hostElement = inject(ElementRef<HTMLElement>);
  readonly config = input<monaco.editor.IStandaloneEditorConstructionOptions>(MonacoEditorComponent.defaultConfig);

  readonly _completionItemProvider = input<monaco.languages.CompletionItemProvider>(null, { alias: 'completionItemProvider' });
  readonly _hoverProvider = input<monaco.languages.HoverProvider>(null, { alias: 'hoverProvider' });
  
  get completionItemProvider() { return this._completionItemProvider(); }
  get hoverProvider() { return this._hoverProvider(); }

  readonly editorContainer = viewChild<ElementRef<HTMLDivElement>>('editorContainer');
  
  readonly value = model<string>('');
  readonly blur = output<void>();

  private _inputValue = signal('');
  private _currentValue = '';

  readonly _monaco = signal<monaco.editor.IStandaloneCodeEditor>(null);
  get monaco() { return this.destroyed ? null : this._monaco(); }

  destroyed = false;
  ignoreValueChange = false;

  public resize() {
    if (!this.checkMonacoAvailable())
      return;

    this.monaco.layout();
  }

  public hasSelection() {
    if (!this.checkMonacoAvailable())
      return false;

    return !this.monaco.getSelection().isEmpty();
  }

  public insertText(value: string) {
    if (!this.checkMonacoAvailable())
      return;

    this.monaco.executeEdits(MONACO_EDITOR_SOURCE, [
      {
        text: value,
        range: this.monaco.getSelection(),
        forceMoveMarkers: true
      }
    ]);
  }

  public replaceText(range: monaco.IRange, newValue: string) {
    if (!this.checkMonacoAvailable())
      return;

    this.monaco.executeEdits(MONACO_EDITOR_SOURCE, [
      {
        text: newValue,
        range,
        forceMoveMarkers: true,
      }
    ]);
  }


  public surroundSelectionWith(prefix: string, suffix: string) {
    if (!this.checkMonacoAvailable())
      return;

    let selection = this.monaco.getSelection();

    this.monaco.executeEdits(MONACO_EDITOR_SOURCE, [
      {
        text: prefix,
        range: selection.collapseToStart(),
        forceMoveMarkers: true
      },
      {
        text: suffix,
        range: selection.collapseToEnd(),
        forceMoveMarkers: true
      }
    ]);
  }

  public clear() {
    if (!this.checkMonacoAvailable())
      return;

    this.monaco.executeEdits(MONACO_EDITOR_SOURCE, [
      {
        text: '',
        range: new monaco.Range(0, 0, Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER)
      }
    ]);
  }

  public setValue(value: string) {
    untracked(() => {
      this.value.set(value);
    });
  }

  public getValue() {
    if (!this.checkMonacoAvailable())
      return;

    return this.monaco.getValue();
  }

  public setMarkers(markers: monaco.editor.IMarkerData[]) {
    if (!this.checkMonacoAvailable())
      return;

    monaco.editor.setModelMarkers(this.monaco.getModel(), MONACO_EDITOR_SOURCE, markers);
  }

  private checkMonacoAvailable() {
    if (!this.monaco) {
      LOGGER.error(`getValue() cannot be called, because monaco is not set.`);
      return false;
    }

    return true;
  }


  constructor(private ngZone: NgZone) {
    effect(() => {
      let _ = [this.editorContainer(), this.monaco];
      untracked(() => {
        if (this.editorContainer()?.nativeElement && !this.monaco) {
          this.createEditor();
        }
      });
    });

    effect(() => {
      let _ = [this.config(), this.monaco];
      untracked(() => {
        if (this.monaco) {
          this.ngZone.runOutsideAngular(() => {
            this.monaco.updateOptions(this.config());
          });
        }
      });
    });

    effect(() => {
      this._inputValue.set(this.value());
    });

    effect(() => {
      let _ = [this.monaco, this._inputValue()];
      untracked(() => {
        this.applyValueToEditorDebounced();
      });
    });
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.stopAutoSize();
    this.disposeEditor();
  }

  ngOnInit() {
  }

  ngAfterViewInit() {
    this.startAutoSize();
  }

  private disposeEditor() {
    if (this.monaco) {
      this.monaco.dispose();
      this._monaco.set(null);
    }
  }

  private createEditor() {
    // Run outside angular zone. Otherwise, the zone will catch events triggered by the editor, which will cause huge performance degradation.
    this.ngZone.runOutsideAngular(() => {
      this._monaco.set(monaco.editor.create(this.editorContainer().nativeElement, this.config()));

      let model = this.monaco.getModel();
      insightsQueryExpressionLanguage.assignComponentToModel(model, this);

      this.monaco.onDidChangeModelContent(e => {
        if (!this.ignoreValueChange)
          this.valueChangedFromEditor();
      });

      this.monaco.onDidBlurEditorWidget(() => {
        this.ngZone.run(() => this.blur.emit());
      });

      this.installEditorHacks();

      this.ensureFontMeasures();
    });
  }

  private installEditorHacks() {
    if (_editorHacksInstalled)
      return;

    _editorHacksInstalled = true;

    // The following hack allows us to set the [title] attribute of a suggestion item completion
    let renderer = suggestWidgetRenderer.ItemRenderer;

    let renderElement = <Function>renderer.prototype.renderElement;
    renderer.prototype.renderElement = function (element: any, index: any, data: any) {
      let __result = renderElement.call(this, element, index, data);

      data.left.title = element.completion.title;

      return __result;
    };


    // HACK: Allow HTML content in hover popups
    let ViewContentWidgets = contentWidgets.ViewContentWidgets;

    let ViewContentWidgets_render = ViewContentWidgets.prototype.render;
    ViewContentWidgets.prototype.render = function (ctx) {
      ViewContentWidgets_render.call(this, ctx);

      let hoverWidget = this._widgets['editor.contrib.contentHoverWidget'];
      if (hoverWidget) {
        setTimeout(() => {
          let baseEl = hoverWidget.domNode.domNode;
          if (baseEl.classList.contains('hidden'))
            return;

          let hoverContents = <any[]>Array.from(hoverWidget.domNode.domNode.querySelectorAll('.hover-contents'));
          if (!hoverContents)
            return;

          for (let hoverContent of hoverContents) {
            if (hoverContent.classList.contains('__content-replaced'))
              return;

            let content = hoverContent.firstChild.textContent;

            hoverContent.firstChild.innerHTML = content;

            hoverContent.classList.add('__content-replaced');
          }
        });
      }
    };
  }

  private ensureFontMeasures() {
    if ('fonts' in document) {
      (document as any).fonts.ready.then(() => {
        monaco.editor.remeasureFonts();
      });
    }
    else {
      setTimeout(() => {
        monaco.editor.remeasureFonts();
      });
    }
  }

  private applyValueToEditorDebounced = debounced(() => {
    untracked(() => {
      if (this._inputValue() == this._currentValue)
        return;

      this._currentValue = this._inputValue();
      this.ngZone.runOutsideAngular(() => {
        this.ignoreValueChange = true;

        try {
          this.monaco?.setValue(this._currentValue);
        }
        finally {
          this.ignoreValueChange = false;
        }
      });
    });
  }, 100);

  private valueChangedFromEditor() {
    if (this.ignoreValueChange)
      return;

    this.ngZone.run(() => {
      untracked(() => {
        this._currentValue = this.monaco?.getValue();
        if (this._currentValue == this._inputValue())
          return;

        this.value.set(this._currentValue);
      });
    });
  }

  //#region Size
  readonly resizeDebounceTime = input(50);
  readonly resizeMaxWaitTime = input(500);

  private monacoLayoutDebounced: Function & { cancel: Function; };

  private readonly updateMonacoLayoutDebounced = effect((onCleanup) => {
    this.monacoLayoutDebounced = debounce(
      () => {
        if (this.destroyed)
          return;

        this.monaco?.layout();
      },
      this.resizeDebounceTime(),
      { maxWait: this.resizeMaxWaitTime() });

    onCleanup(() => this.cancelMonacoLayoutDebounced());
  });

  private cancelMonacoLayoutDebounced() {
    try {
      this.monacoLayoutDebounced?.cancel();
    }
    finally {
      this.monacoLayoutDebounced = null;
    }
  }
  //#endregion

  //#region Auto Sizing
  private autoSizeStarted = false;
  private resizeObserver: ResizeObserver;
  private windowResizeCallback: any;

  private startAutoSize() {
    if (this.autoSizeStarted)
      return;

    this.autoSizeStarted = true;

    try {
      this.ngZone.runOutsideAngular(() => {
        this.windowResizeCallback = () => this.monacoLayoutDebounced();
        window.addEventListener('resize', this.windowResizeCallback);
      });
    }
    catch (err) {
      LOGGER.error(err);
    }
    finally {
      try {
        this.resizeObserver = new ResizeObserver(() => this.monacoLayoutDebounced());
        this.resizeObserver.observe(this.hostElement.nativeElement, { box: 'border-box' });
      }
      catch (err) {
        LOGGER.error(err);
      }
      finally {
        this.monacoLayoutDebounced();
      }
    }
  }

  private stopAutoSize() {
    if (!this.autoSizeStarted)
      return;

    this.autoSizeStarted = false;

    try {
      this.resizeObserver?.disconnect();
    }
    finally {
      this.resizeObserver = null;

      try {
        if (this.windowResizeCallback)
          window.removeEventListener('resize', this.windowResizeCallback);
      }
      finally {
        this.windowResizeCallback = null;
      }
    }
  }
  //#endregion
}

monaco.editor.addKeybindingRule({
  keybinding: monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyF,
  command: null
});
